/**
 * Product gallery zoom/lightbox — capture-phase handler so clicks are not blocked
 * by carousel drag logic or other bubbling listeners.
 */
import { PhotoSwipeLightbox } from "vendor";

const lightboxByGallery = new WeakMap();

function getCarousel(gallery) {
  return gallery.querySelector(".product-gallery__carousel");
}

function getGalleryImages(gallery) {
  const carousel = getCarousel(gallery);
  if (!carousel) {
    return [];
  }
  return Array.from(carousel.children)
    .filter(
      (cell) =>
        cell.matches(".product-gallery__media") &&
        cell.getAttribute("data-media-type") === "image" &&
        !cell.hasAttribute("hidden")
    )
    .map((cell) => cell.querySelector("img:not([aria-hidden='true'])"))
    .filter(Boolean);
}

function getHighResSrc(image) {
  const src = image.currentSrc || image.src;
  if (!src) {
    return src;
  }
  if (src.includes("width=")) {
    return src.replace(/width=\d+/g, "width=3000");
  }
  return src.replace(/_(\d+)x(\d+|\*)?\./, "_3000x.");
}

function buildDataSource(images) {
  return images.map((image) => {
    const width = Math.max(parseInt(image.getAttribute("width"), 10) || image.naturalWidth || 2000, 1);
    const height = Math.max(parseInt(image.getAttribute("height"), 10) || image.naturalHeight || 2000, 1);
    const src = getHighResSrc(image);
    return {
      thumbnailElement: image,
      src,
      srcset: image.srcset,
      msrc: image.currentSrc || image.src,
      width,
      height,
      w: width,
      h: height,
      alt: image.alt,
      thumbCropped: true
    };
  });
}

function registerLightboxUi(lightbox) {
  lightbox.on("uiRegister", () => {
    const { pswp } = lightbox;
    if (!pswp) {
      return;
    }
    const strings = window.themeVariables?.strings || {};
    pswp.ui.registerElement({
      name: "close-button",
      className: "circle-button circle-button--xl hover:animate-icon-block",
      ariaLabel: strings.closeGallery || "Close",
      order: 2,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
      onClick: () => pswp.close()
    });
    if (pswp.options.dataSource.length > 1) {
      pswp.ui.registerElement({
        name: "previous-button",
        className: "circle-button hover:animate-icon-inline",
        ariaLabel: strings.previous || "Previous",
        order: 1,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => pswp.prev()
      });
      pswp.ui.registerElement({
        name: "next-button",
        className: "circle-button hover:animate-icon-inline",
        ariaLabel: strings.next || "Next",
        order: 3,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => pswp.next()
      });
    }
  });
}

function getLightbox(gallery) {
  if (lightboxByGallery.has(gallery)) {
    return lightboxByGallery.get(gallery);
  }
  const lightbox = new PhotoSwipeLightbox({
    pswpModule: () => import("photoswipe"),
    bgOpacity: 1,
    maxZoomLevel: parseInt(gallery.getAttribute("allow-zoom"), 10) || 3,
    closeTitle: window.themeVariables?.strings?.closeGallery,
    zoomTitle: window.themeVariables?.strings?.zoomGallery,
    errorMsg: window.themeVariables?.strings?.errorGallery,
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    close: false,
    zoom: false
  });
  lightbox.addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement);
  registerLightboxUi(lightbox);
  lightbox.init();
  lightboxByGallery.set(gallery, lightbox);
  return lightbox;
}

function openGalleryLightbox(gallery, index) {
  const images = getGalleryImages(gallery);
  if (!images.length) {
    return;
  }
  const dataSource = buildDataSource(images);
  let openIndex = index;
  if (openIndex === undefined || openIndex === null || openIndex < 0) {
    openIndex = 0;
  }
  openIndex = Math.min(openIndex, dataSource.length - 1);
  if (window.pswp) {
    try {
      window.pswp.destroy();
    } catch (error) {
      /* ignore */
    }
  }
  const lightbox = getLightbox(gallery);
  lightbox.loadAndOpen(openIndex, dataSource);
}

function isDesktopImageZoom() {
  const mdQuery = window.themeVariables?.mediaQueries?.md || "screen and (min-width: 1000px)";
  return window.matchMedia(mdQuery).matches;
}

function onDocumentClick(event) {
  const gallery = event.target.closest("product-gallery[allow-zoom]");
  if (!gallery) {
    return;
  }

  if (event.target.closest(".product-gallery__zoom-button")) {
    event.preventDefault();
    event.stopPropagation();
    openGalleryLightbox(gallery);
    return;
  }

  if (!isDesktopImageZoom()) {
    return;
  }

  const carousel = getCarousel(gallery);
  if (!carousel || !carousel.contains(event.target)) {
    return;
  }

  const image = event.target.closest("img");
  if (!image || image.getAttribute("aria-hidden") === "true") {
    return;
  }

  const media = image.closest(".product-gallery__media");
  if (!media || media.getAttribute("data-media-type") !== "image" || media.hasAttribute("hidden")) {
    return;
  }

  const images = getGalleryImages(gallery);
  const openIndex = images.indexOf(image);
  if (openIndex < 0) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  openGalleryLightbox(gallery, openIndex);
}

function patchProductGallery() {
  customElements.whenDefined("product-gallery").then(() => {
    const ProductGallery = customElements.get("product-gallery");
    if (!ProductGallery?.prototype?.openLightBox) {
      return;
    }
    ProductGallery.prototype.openLightBox = function (index) {
      openGalleryLightbox(this, index);
    };
  });
}

function init() {
  document.removeEventListener("click", onDocumentClick, true);
  document.addEventListener("click", onDocumentClick, true);
  patchProductGallery();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

document.addEventListener("shopify:section:load", init);

window.openProductGalleryLightbox = openGalleryLightbox;
